import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  pontoApi, rotuloFuncionario, formatarCpf,
  type FuncionarioPonto, type SituacaoFuncionario,
} from "@/features/ponto/pontoApi";

interface FuncionarioComboboxProps {
  /** Id do funcionário selecionado (0/undefined = nenhum). */
  value?: number;
  onChange: (id: number, funcionario?: FuncionarioPonto) => void;
  /** Padrão "ativos". */
  situacao?: SituacaoFuncionario;
  departamentoId?: number;
  cargoId?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Mostra o "x" para limpar a seleção (ex.: filtros). */
  permitirLimpar?: boolean;
  className?: string;
}

/**
 * Seleção de funcionário do Ponto com busca no servidor por nome, matrícula ou CPF (sem
 * acento nem maiúsculas). Exibe "Matrícula — Nome"; o id interno nunca aparece.
 * Mesmo padrão do `PessoaSearchCombobox` — migra para o `EntityCombobox` do PKG-00.
 */
export const FuncionarioCombobox = ({
  value,
  onChange,
  situacao = "ativos",
  departamentoId,
  cargoId,
  placeholder = "Selecione o funcionário...",
  disabled,
  permitirLimpar,
  className,
}: FuncionarioComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const busca = useDebounce(search, 250);

  const { data, isFetching } = useQuery({
    queryKey: ["ponto-funcionarios-busca", busca, situacao, departamentoId, cargoId],
    queryFn: () => pontoApi.pesquisarFuncionarios({
      busca, situacao, departamentoId, cargoId, tamanhoPagina: 30,
    }),
    enabled: open,
    staleTime: 15_000,
  });
  const resultados = data?.itens ?? [];

  // Rótulo do valor salvo (pode não estar na página de resultados).
  const { data: selecionado } = useQuery({
    queryKey: ["ponto-funcionario", value],
    queryFn: async () => (await pontoApi.pesquisarFuncionarios({ ids: [value!], tamanhoPagina: 1 })).itens[0] ?? null,
    enabled: !!value,
    staleTime: 60_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative w-full", className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate", !selecionado && "text-muted-foreground")}>
              {value && selecionado ? rotuloFuncionario(selecionado) : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {permitirLimpar && !!value && !disabled && (
          <button
            type="button"
            aria-label="Limpar funcionário"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); onChange(0); }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[420px] max-w-[calc(100vw-2rem)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nome, matrícula ou CPF..." value={search} onValueChange={setSearch} />
          <CommandList>
            {isFetching && resultados.length === 0 && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isFetching && <CommandEmpty>Nenhum funcionário encontrado.</CommandEmpty>}
            <CommandGroup>
              {resultados.map((f) => (
                <CommandItem
                  key={f.id}
                  value={String(f.id)}
                  onSelect={() => { onChange(f.id, f); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === f.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate">{rotuloFuncionario(f)}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {[f.sCnpjCpf ? `CPF ${formatarCpf(f.sCnpjCpf)}` : null, f.sDepartamento || null, f.sCargo || null]
                        .filter(Boolean).join(" · ") || "—"}
                    </span>
                  </div>
                  {!f.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}
                </CommandItem>
              ))}
            </CommandGroup>
            {data && data.total > resultados.length && (
              <p className="px-3 py-2 text-xs text-muted-foreground border-t">
                Mostrando {resultados.length} de {data.total} — refine a busca.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
